import { redirect } from '@tanstack/react-router';
import type { ParsedLocation } from '@tanstack/react-router';

import { setCookie } from 'shared/client/utils/cookies';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from 'shared/common/consts/supportedLanguages';
import { UI_LANGUAGE_COOKIE } from 'shared/utils/consts';
import { getEnvVar } from 'shared/utils/getEnvVar';
import { isAbsoluteURL, verifyHostName } from 'shared/utils/url.utils';

function getAllowedDomains(): string[] | null {
  const allowedDomainsEnv = getEnvVar('VITE_ALLOWED_REDIRECT_DOMAINS', { throwOnMissing: false });
  if (allowedDomainsEnv) {
    return allowedDomainsEnv.split(',').map(d => d.trim());
  }
  return null;
}

export function isValidRedirect(redirectURL: string, whitelistDomains: string[] | null): boolean {
  if (isAbsoluteURL(redirectURL)) {
    const normalizedURL = redirectURL.startsWith('//') ? `https:${redirectURL}` : redirectURL;
    return !!whitelistDomains?.length && verifyHostName(normalizedURL, whitelistDomains);
  }
  if (/^[\w-]+\.[\w.-]+(?:\/|$)/.test(redirectURL)) {
    return false;
  }
  return true;
}

export function handleLanguageFromQuery(searchParams: URLSearchParams): void {
  const language = searchParams.get('lang');
  if (language) {
    const finalLanguage = (SUPPORTED_LANGUAGES as readonly string[]).includes(language) ? language : DEFAULT_LANGUAGE;
    setCookie(UI_LANGUAGE_COOKIE, finalLanguage);
  }
}

export function handleCourseIdFromQuery(searchParams: URLSearchParams): void {
  const courseDraftId = searchParams.get('courseDraftId');
  if (courseDraftId) {
    sessionStorage.setItem('x-course-id', courseDraftId);
  }
}

export function validateRedirectToParam(search: Record<string, unknown>, searchParams: URLSearchParams): void {
  const redirectTo = searchParams.get('redirectTo') || (search.redirectTo as string | undefined);
  if (redirectTo && !isValidRedirect(redirectTo, getAllowedDomains())) {
    const newSearch = { ...search };
    delete newSearch.redirectTo;
    throw redirect({ to: '/', search: newSearch });
  }
}

export function runGlobalSideEffects(location: ParsedLocation): void {
  const search = location.search as Record<string, unknown>;
  const searchParams = new URLSearchParams(location.searchStr ?? '');

  handleLanguageFromQuery(searchParams);
  handleCourseIdFromQuery(searchParams);
  validateRedirectToParam(search, searchParams);
}
